import { CommonModule, DatePipe } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  inject,
  output,
  ViewChild,
  ChangeDetectionStrategy,
} from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { MatPaginatorModule } from '@angular/material/paginator';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatSortModule } from '@angular/material/sort';
import { MatTable, MatTableModule } from '@angular/material/table';
import { MatTooltip } from '@angular/material/tooltip';
import { CompanyIdToCompanyPipe } from '@app/features/companies/company-id-to-company-pipe';
import { CompanyNamePipe } from '@app/features/companies/company-name-pipe';
import { AddDocumentToContract } from '@app/features/contracts/add-document-to-contract/add-document-to-contract';
import { AmendmentLauncher } from '@app/features/amendments/amendment-launcher.service';
import { AmendmentActionButtons } from '@app/features/amendments/amendment-action-buttons/amendment-action-buttons';
import { AmendmentService } from '@app/features/amendments/amendment.service';
import { ContractService } from '@app/features/contracts/contract-service';
import { ContractStatusComponent } from '@app/features/contracts/contract-status/contract-status';
import {
  Contract,
  ContractListItem,
  ContractSearch,
  toContractListItem,
} from '@app/features/contracts/contract.model';
import { ButtonContractHistory } from '@app/features/contracts/button-contract-history/button-contract-history';
import { ButtonContractPdf } from '@app/features/contracts/button-contract-pdf/button-contract-pdf';
import { ContractActionButtons } from '@app/features/contracts/contract-action-buttons/contract-action-buttons';
import { MenuContractOfSerie } from '@app/features/contracts/menu-contract-of-serie/menu-contract-of-serie';
import { DocumentService } from '@app/features/documents/document-service';
import { DocumentTagLabelPipe } from '@app/features/documents/document-tag-label-pipe';
import { DocumentTag } from '@app/features/documents/document.model';
import { AUTHENTICATED_USER_SERVICE } from '@app/shared/auth/authenticated-user-service/authenticated-user-service.token';
import { ControlsOf } from '@app/shared/forms/controls-of.form-group';
import { TextIcon } from '@app/shared/icons/text-icon/text-icon';
import { FrDatePipe } from '@app/shared/pipes/fr-time.pipe copy';
import { SortableAndPageableTableController } from '@app/shared/table.controller';
import { LucideEye, LucideFilePenLine, LucidePlay } from '@lucide/angular';
import { switchMap } from 'rxjs';
import { DocumentTagToTextIconPipe } from '../../documents/document-tag-to-text-icon-pipe';
import { SignatureTh } from '../signature/signature-th/signature-th';
import { YousignSummaryCell } from '../signature/yousign-summary-cell/yousign-summary-cell';
import { SignatureSendFailure } from '@app/features/contracts/signature/signature-send-failure/signature-send-failure';

const contractTableColumns = [
  'employee.lastname',
  'employee.firstname',
  'company.name',
  'contract.contractType',
  'contract.startDate',
  'contract.endDate',
  'contract.endOfFirstTrialPeriodDate',
  'contract.documentTags',
  'status',
  'adpEditedAt',
  'signatureRequests',
  'actions',
] as const;
type ContractTableColumn = (typeof contractTableColumns)[number];

@Component({
  selector: 'app-contracts-table',
  imports: [
    CommonModule,
    MatTableModule,
    MatSortModule,
    FrDatePipe,
    MatIconModule,
    MatButtonModule,
    MatTooltip,
    CompanyNamePipe,
    MatMenuModule,
    CompanyIdToCompanyPipe,
    ContractStatusComponent,
    AddDocumentToContract,
    LucideEye,
    LucideFilePenLine,
    LucidePlay,
    MenuContractOfSerie,
    ContractActionButtons,
    ButtonContractHistory,
    ButtonContractPdf,
    TextIcon,
    DocumentTagToTextIconPipe,
    MatPaginatorModule,
    YousignSummaryCell,
    SignatureSendFailure,
    SignatureTh,
    MatProgressBarModule,
    AmendmentActionButtons,
  ],
  templateUrl: './contracts-table.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./contracts-table.scss'],
})
export class ContractsTable
  extends SortableAndPageableTableController<ContractListItem, ContractSearch, ContractTableColumn>
  implements AfterViewInit
{
  editContract = output<ContractListItem>();
  editSocialSecurity = output<ContractListItem>();

  /** Un CDI ou un CDD A ni annulé ni terminé, sans avenant en cours ; l'API tranche au clic. */
  mayReceiveAmendment(contract: ContractListItem): boolean {
    return AmendmentLauncher.mayReceiveAmendment(contract);
  }

  establishAmendment(contract: ContractListItem): void {
    this.register(
      this.amendmentLauncher
        .establishOnContract(contract.id)
        .pipe(switchMap((dialogRef) => dialogRef.afterClosed()))
        .subscribe(() => this.filter()),
    );
  }

  /** L'avenant a pu changer d'état, ou être abandonné : la page se relit à la fermeture. */
  consultAmendment(contract: ContractListItem): void {
    const amendment = contract.amendmentInProgress;
    if (!amendment) {
      return;
    }
    this.register(
      this.amendmentLauncher
        .consult(contract.id, amendment.id)
        .pipe(switchMap((dialogRef) => dialogRef.afterClosed()))
        .subscribe(() => this.filter()),
    );
  }

  public override searchFn = (search: Partial<ContractSearch>) => this.contractService.list(search);
  public override sortBy?: string;
  public override displayedColumns = [...contractTableColumns];
  override readonly cdr = inject(ChangeDetectorRef);
  readonly dialog = inject(MatDialog);
  readonly amendmentService = inject(AmendmentService);

  readonly datePipe = new DatePipe('fr-FR');
  readonly documentTagLabelPipe = new DocumentTagLabelPipe();

  readonly authenticatedUserService = inject(AUTHENTICATED_USER_SERVICE);
  readonly contractService = inject(ContractService);
  private readonly amendmentLauncher = inject(AmendmentLauncher);
  readonly documentService = inject(DocumentService);

  readonly optionalDocuments: DocumentTag[] = ['rib', 'socialSecurityCertificate'] as const;
  readonly luEye = LucideEye;

  @ViewChild(MatTable) table!: MatTable<ContractListItem>;
  selectedContract: ContractListItem | null = null;

  constructor() {
    super();
  }

  public override createSearchForm(): FormGroup<ControlsOf<ContractSearch>> {
    return new FormGroup<ControlsOf<ContractSearch>>({
      companyId: new FormControl<ContractSearch['companyId']>(null),
      createdByUserId: new FormControl<ContractSearch['createdByUserId']>(null),
      employeeFirstname: new FormControl<ContractSearch['employeeFirstname']>(null),
      employeeLastname: new FormControl<ContractSearch['employeeLastname']>(null),
      pendingValidationRequestRequestedToUserId: new FormControl<
        ContractSearch['pendingValidationRequestRequestedToUserId']
      >(null),
      statuses: new FormControl<ContractSearch['statuses']>(null),
      amendmentInProgress: new FormControl<ContractSearch['amendmentInProgress']>(null),
      deleted: new FormControl<ContractSearch['deleted']>(false),
    });
  }

  /** L'info-bulle de la coche « Édité » d'un contrat, ou de son avenant en cours. */
  adpEditedTooltip(
    contract: Partial<Pick<ContractListItem, 'adpEditedAt' | 'adpEditedBy'>>,
  ): string {
    let tooltip = 'Édité par ';
    tooltip += contract.adpEditedBy ?? '?';
    tooltip += ' le ';
    tooltip += contract.adpEditedAt
      ? this.datePipe.transform(contract.adpEditedAt, 'EEEE dd MMM yyyy à HH:mm:ss')
      : '?';
    return tooltip;
  }

  ngAfterViewInit(): void {
    Promise.resolve().then(() => this.filter());
    this.register(
      this.contractService.created$.subscribe((contract) => {
        this.updateRow(null, this.toListItem(contract));
      }),
      this.contractService.updated$.subscribe((contract) => {
        this.updateRow(
          this.dataSource.find((c) => c.id === contract.id) ?? null,
          this.toListItem(contract),
        );
      }),
      // Un geste sur l'avenant d'une ligne — validation, signature, dépôt, abandon — relit
      // l'avenant : la ligne se relit avec lui (RG-avenant-liste-des-contrats).
      this.amendmentService.updated$.subscribe((amendment) => {
        if (this.dataSource.some((row) => row.amendmentInProgress?.id === amendment.id)) {
          this.filter();
        }
      }),
      this.contractService.deleted$.subscribe((contractId) => {
        const index = this.dataSource.findIndex((c) => c.id === contractId);
        if (index !== -1) {
          this.dataSource.splice(index, 1);
          this.table.renderRows();
        }
      }),
    );
  }

  edit(item: ContractListItem): void {
    this.editContract.emit(item);
  }

  /** Le contrat complet ne dit rien de ses avenants : la ligne garde celui qu'elle montrait. */
  toListItem(contract: Contract): ContractListItem {
    const current = this.dataSource.find((row) => row.id === contract.id);
    return {
      ...toContractListItem(contract),
      amendmentInProgress: current?.amendmentInProgress ?? null,
    };
  }

  hasDocument(element: ContractListItem, tag: DocumentTag): boolean {
    return element.documentTags?.includes(tag) ?? false;
  }

  onOpenUserMenu(element: ContractListItem) {
    this.selectedContract = element;
  }

  refreshRow(contract: ContractListItem) {
    this.contractService.findOne(contract.id).subscribe(() => {
      // No-op, update row will be triggered by the contractService.updated$ subscription
    });
  }
}
